import React, { useEffect, useState } from "react";

import {
  Anchor,
  Button,
  Card,
  Divider,
  Modal,
  Stack,
  Text,
  TextInput,
  TextInputProps,
} from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import LogoCircle from "app/ui/assets/logo/logo-circle.svg";
import { GoogleSignIn } from "app/ui/components/auth/GoogleSignIn";
import Image from "next/image";

import { RQ_USER_QUERY_KEY } from "components/AuthProvider/AuthProvider";

import { useTrackerAuth } from "./AuthProvider";

interface RegistrationModalProps {
  opened: boolean;
  toggle: () => void;
}

export function RegistrationModal(props: RegistrationModalProps): JSX.Element {
  const [email, setEmail] = React.useState("");
  const [name, setName] = React.useState("");
  const [error, setError] = React.useState("");
  const [loading, setLoading] = React.useState(false);

  const [isEmailValid, setIsEmailValid] = useState(true);
  const [hasSubmitted, setHasSubmitted] = useState<"no" | "attempt" | "success">("no");

  const [successMessage, setSuccessMessage] = useState("");

  const { refresh } = useTrackerAuth();
  const queryClient = useQueryClient();

  function onLoginSuccess() {
    refresh();
    props.toggle();
  }

  const onSubmit = async () => {
    if (hasSubmitted === "success") {
      return;
    }

    setHasSubmitted("attempt");
    if (loading || !isEmailValid) {
      return;
    }

    setLoading(true);

    setError("");
    try {
      const response = await fetch("/api/v1/auth/register/benchmarking", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, name }),
      });

      if (response.status === 400) {
        const data = await response.json();
        setError(data.message);
      } else {
        setHasSubmitted("success");
        setSuccessMessage(
          "Thanks for signing up! Please check your email to set a password. You'll need to log in with password to continue using Comprehensive Benchmarking.",
        );

        // @ts-expect-error TS2769
        await queryClient.invalidateQueries(RQ_USER_QUERY_KEY);
      }
    } catch (error) {
      console.error(error);
    }

    setLoading(false);
  };

  return (
    <Modal
      centered
      withCloseButton={false}
      opened={props.opened}
      onClose={props.toggle}
      size={360}
      styles={{
        title: {
          textAlign: "center",
        },
        content: {
          borderRadius: "8px",
        },
      }}
    >
      <Card withBorder={false}>
        <Stack w="100%" align="center" gap={10}>
          <Image {...LogoCircle} width={32} height={32} alt="Comprehensive" />
          <Stack w="100%" align="center" mb={4}>
            <Text fz={18} fw="bold">
              Benchmark For Free
            </Text>
            <Text fz={14} c="gray.5" ta="center">
              Sign up for free to continue browsing our real-time benchmarking data.
            </Text>
          </Stack>
          <GoogleSignIn
            buttonProps={{ size: "sm" }}
            buttonText="Login with Google Workplace"
            source="benchmarking"
            onLoginSuccess={onLoginSuccess}
          />
          <Divider my="md" label="OR" labelPosition="center" color="gray.4" w="100%" />
          <TextInput
            w="100%"
            placeholder="Enter your name"
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
          />
          <EmailInput
            w="100%"
            placeholder="Enter your work email"
            value={email}
            type="email"
            error={!isEmailValid && hasSubmitted === "attempt" ? "Invalid Email" : undefined}
            onValidChange={setIsEmailValid}
            onChange={(e) => setEmail(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && email && name && !loading) {
                onSubmit();
              }
            }}
          />

          <Button
            fullWidth
            loading={loading}
            disabled={!email || !name || loading || hasSubmitted === "success"}
            data-heap-id="benchmarking-email-reg-button"
            onClick={onSubmit}
            variant="primary"
          >
            Get started
          </Button>
          {error && (
            <Text c="red" fz="xs" ta="center">
              {error}
            </Text>
          )}
          {successMessage && (
            <Text c="green" fz="xs" ta="center">
              {successMessage}
            </Text>
          )}
          <Text size="sm">
            Already have an account?{" "}
            <Anchor href="/login" sx={{ textDecoration: "none" }}>
              Log in
            </Anchor>
          </Text>
        </Stack>
      </Card>
    </Modal>
  );
}

interface EmailInputProps extends TextInputProps {
  onValidChange?: (isValid: boolean) => void;
}

const EmailInput: React.FC<EmailInputProps> = ({ onValidChange, ...props }) => {
  const [isValid, setIsValid] = useState(true);

  useEffect(() => {
    if (onValidChange) {
      onValidChange(isValid);
    }
  }, [isValid, onValidChange]);

  return (
    <TextInput
      {...props}
      onChange={(e) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const validEmail = emailRegex.test(e.currentTarget.value);
        setIsValid(validEmail);

        if (props.onChange) {
          props.onChange(e);
        }
      }}
    />
  );
};
